/**
 * 按访问设备把站内链接改写成 0 跳地址（点击后不再经过 301 / 302）。
 *
 * 规则由 umu_node 下发到 window.pageData.config.deviceLinkRules
 * （umu_node app/utils/singleDomAdaptedPages.ts getDeviceLinkRules），这里只负责套用。
 * umu_module 的 home-header / home-footer / simple-footer 及 umu_home、umu_service_pages 各有一份同样的逻辑，修改时需同步。
 *
 * 改写结果（以 umu.cn 为例）：
 *
 *   输入                              PC 访问                                  手机访问
 *   /product/ai-literacy              https://www.umu.cn/product/ai-literacy   https://m.umu.cn/product/ai-literacy
 *   /blog/（在 www 名单里）           https://www.umu.cn/blog/                 https://www.umu.cn/blog/
 *   https://m.umu.cn/es/home?x=1      https://www.umu.cn/es/home?x=1           https://m.umu.cn/es/home?x=1
 *   https://m.umu.cn/course/pay?g=1   https://m.umu.cn/course/pay?g=1          https://m.umu.cn/course/pay?g=1
 *   （在 m 名单里，PC 也只能走 m.）
 *   站外链接、#xxx、javascript:、不以 / 开头的相对路径    原样返回
 *
 * 测试环境（dev54.umucdn.cn 等）不改写，见 getBareHost。
 */
export interface IDeviceLinkRules {
    /** 当前访问是否为移动端（由 umu_node 按 UA 判断） */
    isMobile: boolean;
    /** 所有设备都走 www. 的路径（精确匹配，不带语言前缀、不带结尾斜杠） */
    wwwPaths: string[];
    /** 所有设备都走 www. 的路径前缀（该路径本身及其子路径） */
    wwwPrefixes: string[];
    /** 移动端路径映射：www 路径 → m 站对应路径 */
    mobilePathMap: Record<string, string>;
    /**
     * 所有设备都走 m. 的路径（PC 访问 www. 也会被跳到 m.）。按路径段匹配：`:id` 匹配任意一段，结尾 `/*` 匹配其下任意子路径。
     * 可选：旧版 umu_node 不下发时视为空。
     */
    mobileOnlyPaths?: string[];
    /** 多语前缀，如 es、fr */
    langPrefixes: string[];
}

/**
 * 取裸域名：https://www.umu.cn → umu.cn。
 * 只有两段式线上域名（umu.cn / umu.com / umu.co）才改写；dev54.umucdn.cn 这类测试域名返回 null。
 */
function getBareHost(siteHost?: string): string | null {
    const bare = (siteHost || '').replace(/^https?:\/\//, '').replace(/^(www|m)\./, '');
    return bare.split('.').length === 2 ? bare : null;
}

/**
 * 取出本站链接的"路径 + 查询/锚点"部分：
 *   https://m.umu.cn/es/home?x=1 → /es/home?x=1
 *   /product/xxx                 → /product/xxx
 * 站外链接、#、javascript:、不以 / 开头的相对路径返回 null（不处理）。
 */
function getSitePathAndQuery(url: string, bare: string): string | null {
    const absolute = /^(?:https?:)?\/\/([^/?#]+)(.*)$/i.exec(url);
    if (absolute) {
        const host = absolute[1].toLowerCase();
        const isSameSite = host === bare || host === `www.${bare}` || host === `m.${bare}`;
        return isSameSite ? absolute[2] || '/' : null;
    }
    return url.startsWith('/') ? url : null;
}

/** 拆开路径和查询/锚点：/es/home?x=1#a → { path: '/es/home', suffix: '?x=1#a' } */
function splitSuffix(pathAndQuery: string): { path: string; suffix: string } {
    const at = pathAndQuery.search(/[?#]/);
    return at < 0
        ? { path: pathAndQuery, suffix: '' }
        : { path: pathAndQuery.slice(0, at), suffix: pathAndQuery.slice(at) };
}

/**
 * 得到用来匹配规则的业务路径：去掉语言前缀和结尾斜杠。
 *   /es/blog/ → { langPrefix: '/es', bizPath: '/blog' }
 *   /es       → { langPrefix: '/es', bizPath: '/' }
 */
function getBizPath(path: string, langPrefixes: string[]): { langPrefix: string; bizPath: string } {
    const firstSegment = path.split('/')[1];
    const langPrefix = firstSegment && langPrefixes.includes(firstSegment) ? `/${firstSegment}` : '';
    let bizPath = langPrefix ? path.slice(langPrefix.length) || '/' : path;
    if (bizPath.length > 1 && bizPath.endsWith('/')) {
        bizPath = bizPath.slice(0, -1);
    }
    return { langPrefix, bizPath };
}

/** 该路径是否所有设备都走 www.（在 wwwPaths 里，或以 wwwPrefixes 中某项开头） */
function isWwwOnlyPath(bizPath: string, rules: IDeviceLinkRules): boolean {
    return (
        rules.wwwPaths.includes(bizPath) ||
        rules.wwwPrefixes.some((prefix) => bizPath === prefix || bizPath.startsWith(`${prefix}/`))
    );
}

/**
 * 路径是否符合模式（按路径段比较）：
 *   /exercise/detail/:id  命中 /exercise/detail/123，不命中 /exercise/detail/123/score
 *   /session/exercise/*   命中 /session/exercise/a、/session/exercise/a/b，不命中 /session/exercise
 */
function matchPathPattern(path: string, pattern: string): boolean {
    const pathSegments = path.split('/');
    const patternSegments = pattern.split('/');
    const hasWildcard = patternSegments[patternSegments.length - 1] === '*';
    const fixedSegments = hasWildcard ? patternSegments.slice(0, -1) : patternSegments;
    if (hasWildcard ? pathSegments.length <= fixedSegments.length : pathSegments.length !== fixedSegments.length) {
        return false;
    }
    return fixedSegments.every((segment, i) => (segment.startsWith(':') ? pathSegments[i] !== '' : segment === pathSegments[i]));
}

/** 该路径是否所有设备都走 m.（PC 访问 www. 也会被跳到 m.） */
function isMobileOnlyPath(bizPath: string, rules: IDeviceLinkRules): boolean {
    return (rules.mobileOnlyPaths || []).some((pattern) => matchPathPattern(bizPath, pattern));
}

/**
 * 把一个链接改写成当前设备的 0 跳地址。
 * 返回 null 表示不适用（未下发规则，或是测试域名），调用方保持原逻辑；
 * 站外链接、相对路径、javascript: 等原样返回。
 */
export function resolveDeviceUrl(url: string, siteHost?: string, rules?: IDeviceLinkRules): string | null {
    const bare = getBareHost(siteHost);
    if (!rules || typeof url !== 'string' || !bare) {
        return null;
    }

    const pathAndQuery = getSitePathAndQuery(url, bare);
    if (pathAndQuery === null) {
        return url;
    }

    const { path, suffix } = splitSuffix(pathAndQuery);
    const { langPrefix, bizPath } = getBizPath(path, rules.langPrefixes);

    // 只在 www 上有的路径：所有设备都走 www.，路径不变
    if (isWwwOnlyPath(bizPath, rules)) {
        return `https://www.${bare}${path}${suffix}`;
    }

    // 只在 m 上有的路径：所有设备都走 m.，路径不变
    if (isMobileOnlyPath(bizPath, rules)) {
        return `https://m.${bare}${path}${suffix}`;
    }

    // PC 访问：走 www.，路径不变
    if (!rules.isMobile) {
        return `https://www.${bare}${path}${suffix}`;
    }

    // 手机访问：走 m.，有映射用映射后的路径（保留语言前缀），否则路径不变
    const mappedPath = rules.mobilePathMap[bizPath];
    return `https://m.${bare}${mappedPath ? langPrefix + mappedPath : path}${suffix}`;
}

/** 深度遍历导航数据，把所有 url 字段按设备改写；不适用时原样返回 */
export function resolveMenuUrls<T>(data: T, siteHost?: string, rules?: IDeviceLinkRules): T {
    if (resolveDeviceUrl('/', siteHost, rules) === null) {
        return data;
    }
    const walk = (node: any): any => {
        if (Array.isArray(node)) {
            return node.map(walk);
        }
        if (node && typeof node === 'object') {
            const out: any = {};
            Object.keys(node).forEach((key) => {
                const value = node[key];
                const isUrlField = key === 'url' && typeof value === 'string';
                out[key] = isUrlField ? resolveDeviceUrl(value, siteHost, rules) ?? value : walk(value);
            });
            return out;
        }
        return node;
    };
    return walk(data);
}

/** umu_node 在 window.pageData.config 下发的设备链接规则；未下发时返回 undefined（链接保持原逻辑） */
export function getDeviceLinkRules(): IDeviceLinkRules | undefined {
    if (typeof window === 'undefined') return undefined;
    return (window as any)?.pageData?.config?.deviceLinkRules || undefined;
}
